import { EmptyStatePanel, ErrorState, LoadingState } from '@playforge/ui';
import { Badge, Banner, BlockStack, Card, DataTable, InlineGrid, InlineStack, Page, Text } from '@shopify/polaris';
import { useNavigate } from 'react-router-dom';
import { ScrollArea } from '../components/ScrollArea';
import { apiClient } from '../lib/api-client';
import { useApi } from '../lib/use-api';
import { syncOrdersQuietly } from '../lib/orders-sync';
import {
  formatMoney,
  formatPercent,
  type CustomersResult,
  type CustomerSummary,
  type MerchantProfile,
} from '../lib/types';

function Metric({ label, value }: { label: string; value: string }): JSX.Element {
  return (
    <Card>
      <BlockStack gap="100">
        <Text as="span" tone="subdued" variant="bodySm">
          {label}
        </Text>
        <Text as="span" variant="headingLg" fontWeight="bold">
          {value}
        </Text>
      </BlockStack>
    </Card>
  );
}

function couponsCell(c: CustomerSummary): JSX.Element {
  if (c.coupons.length === 0) return <Text as="span" tone="subdued">—</Text>;
  const first = c.coupons[0];
  const extra = c.coupons.length - 1;
  return (
    <Text as="span">
      <code>{first?.code}</code>
      {extra > 0 ? <Text as="span" tone="subdued">{` +${extra}`}</Text> : null}
    </Text>
  );
}

export function CustomersPage(): JSX.Element {
  const navigate = useNavigate();
  const data = useApi(async () => {
    await syncOrdersQuietly();
    return apiClient.get<CustomersResult>('/customers');
  }, []);
  // Currency for spend formatting (best-effort; falls back to USD).
  const profile = useApi(() => apiClient.get<MerchantProfile>('/merchant/profile'), []);
  const currency = profile.data?.currency ?? null;

  const rows = (data.data?.items ?? []).map((c) => [
    <InlineStack key={`${c.id}-name`} gap="200" blockAlign="center">
      <Text as="span" fontWeight="semibold">
        {c.label}
      </Text>
      <Badge {...(c.kind === 'customer' ? { tone: 'info' as const } : {})}>
        {c.kind === 'customer' ? 'Customer' : 'Guest'}
      </Badge>
    </InlineStack>,
    String(c.plays),
    String(c.rewardsWon),
    couponsCell(c),
    String(c.couponsRedeemed),
    c.totalSpent > 0 ? formatMoney(c.totalSpent, currency) : '—',
    new Date(c.lastActiveAt).toLocaleDateString(),
  ]);

  const stats = data.data?.stats;

  return (
    <Page title="Customers" subtitle="Players, what they won, and what converted">
      {data.loading ? <LoadingState label="Loading customers" /> : null}
      {data.error ? <ErrorState message={data.error.message} onRetry={data.refetch} /> : null}

      {data.data && data.data.items.length === 0 ? (
        <Card>
          <EmptyStatePanel
            heading="No players yet"
            primaryAction={{ content: 'View campaigns', onAction: () => navigate('/campaigns') }}
          >
            <p>
              Once shoppers play a game on your storefront, they appear here with their plays, prizes
              won, and any orders those coupons drove.
            </p>
          </EmptyStatePanel>
        </Card>
      ) : null}

      {stats && data.data && data.data.items.length > 0 ? (
        <BlockStack gap="500">
          {data.data.truncated ? (
            <Banner tone="info" title="Showing your most recent players">
              <p>
                You have a large volume of activity, so older records aren&apos;t included in these
                totals. Contact support if you need a full export.
              </p>
            </Banner>
          ) : null}
          <InlineGrid columns={{ xs: 2, sm: 3, md: 6 }} gap="300">
            <Metric label="Players" value={stats.totalPlayers.toLocaleString()} />
            <Metric label="Customers" value={stats.customers.toLocaleString()} />
            <Metric label="Guests" value={stats.guests.toLocaleString()} />
            <Metric label="Rewards won" value={stats.rewardsWon.toLocaleString()} />
            <Metric label="Redeemed" value={stats.redemptions.toLocaleString()} />
            <Metric label="Conversion" value={formatPercent(stats.conversionRate)} />
          </InlineGrid>

          <Card padding="0">
            <ScrollArea maxHeight={560}>
              <DataTable
                columnContentTypes={['text', 'numeric', 'numeric', 'text', 'numeric', 'numeric', 'text']}
                headings={['Customer', 'Plays', 'Won', 'Coupon', 'Redeemed', 'Spent', 'Last active']}
                rows={rows}
              />
            </ScrollArea>
          </Card>

          <Text as="p" tone="subdued" variant="bodySm">
            Spend and conversion populate once coupon redemptions are recorded against orders.
            Logged-in shoppers are identified by their Shopify customer id; guests are anonymous.
          </Text>
        </BlockStack>
      ) : null}
    </Page>
  );
}
